import React, { useState, useEffect } from 'react';
import { Auth } from './Auth';
import { BarChart3, TrendingUp, Shield, Users, FileText, Zap, Star, ArrowLeft, CheckCircle, PlayCircle } from 'lucide-react';

export const LandingPage: React.FC = () => {
  const [showAuth, setShowAuth] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % 3);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const features = [
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: "تحليل متطور",
      description: "تحليل شامل لاستبانات ورش العمل مع إحصائيات متقدمة"
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "رؤى عميقة", 
      description: "استخراج رؤى قيمة من البيانات لتطوير ورش العمل"
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "أمان عالي",
      description: "حماية متقدمة للبيانات مع تشفير من الطراز العالمي"
    },
    {
      icon: <FileText className="w-8 h-8" />,
      title: "تقارير احترافية",
      description: "تقارير تفصيلية بتصاميم احترافية قابلة للتخصيص"
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "تعاون جماعي",
      description: "مشاركة النتائج والتعاون مع الفريق بسهولة"
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "سرعة فائقة",
      description: "معالجة البيانات بسرعة البرق مع واجهة متجاوبة"
    }
  ];

  const testimonials = [
    {
      name: "د. أحمد محمد",
      role: "مدير التدريب",
      content: "أداة رائعة ساعدتنا في تطوير ورش العمل بشكل كبير",
      rating: 5
    },
    {
      name: "أ. فاطمة علي", 
      role: "منسقة الجودة",
      content: "التقارير المفصلة والتحليل العميق يوفر رؤى قيمة جداً",
      rating: 5
    },
    {
      name: "م. خالد يوسف",
      role: "أخصائي تطوير",
      content: "واجهة سهلة الاستخدام ونتائج دقيقة ومفيدة",
      rating: 5
    }
  ];

  if (showAuth) {
    return <Auth onClose={() => setShowAuth(false)} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white overflow-hidden" dir="rtl">
      {/* Floating background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
        <div className="absolute top-40 -left-40 w-80 h-80 bg-yellow-500 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-40 left-20 w-80 h-80 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>
      </div>

      {/* Header */}
      <header className={`relative z-10 px-6 py-4 transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : '-translate-y-10 opacity-0'}`}>
        <nav className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-reverse space-x-4">
            <div className="w-10 h-10 bg-gradient-to-r from-purple-400 to-pink-400 rounded-lg flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold">محلل الاستبانات</span>
          </div>
          <button
            onClick={() => setShowAuth(true)}
            className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 px-6 py-2 rounded-full font-medium transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
          >
            تسجيل الدخول
          </button>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="relative z-10 px-6 py-20">
        <div className="max-w-7xl mx-auto text-center">
          <div className={`transition-all duration-1000 delay-300 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-white via-purple-200 to-pink-200 bg-clip-text text-transparent">
              تحليل استبانات
              <br />
              <span className="text-gradient bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text">
                ورش العمل التدريبية
              </span>
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-gray-300 max-w-3xl mx-auto leading-relaxed">
              منصة متقدمة لتحليل استبانات ورش العمل التدريبية واستخراج رؤى قيمة لتطوير وتحسين جودة التدريب
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button
                onClick={() => setShowAuth(true)}
                className="group bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 transform hover:scale-105 hover:shadow-2xl flex items-center gap-3"
              >
                ابدأ التحليل الآن
                <ArrowLeft className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <button className="group border-2 border-white/30 hover:border-white/50 px-8 py-4 rounded-full font-medium text-lg transition-all duration-300 flex items-center gap-3 hover:bg-white/10">
                <PlayCircle className="w-5 h-5" />
                مشاهدة العرض التوضيحي
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative z-10 px-6 py-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-right">الميزات الرئيسية</h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto text-right">
              اكتشف القوة الحقيقية لتحليل البيانات مع مجموعة شاملة من الأدوات المتقدمة
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className={`group p-6 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 transition-all duration-500 transform hover:scale-105 hover:shadow-2xl ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}
                style={{ transitionDelay: `${index * 100 + 600}ms` }}
              >
                <div className="w-16 h-16 bg-gradient-to-r from-purple-400 to-pink-400 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold mb-3 text-right">{feature.title}</h3>
                <p className="text-gray-300 text-right leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="relative z-10 px-6 py-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            {[
              { number: "1000+", label: "ورشة عمل محللة" },
              { number: "50000+", label: "استبانة معالجة" },
              { number: "99.9%", label: "دقة التحليل" },
              { number: "24/7", label: "دعم مستمر" }
            ].map((stat, index) => (
              <div
                key={index}
                className={`transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}
                style={{ transitionDelay: `${index * 200 + 1000}ms` }}
              >
                <div className="text-4xl md:text-5xl font-bold text-transparent bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-300 text-lg">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="relative z-10 px-6 py-20">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-16 text-right">آراء عملائنا</h2>
          
          <div className="relative">
            <div className="overflow-hidden rounded-2xl">
              <div 
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(${currentSlide * 100}%)` }}
              >
                {testimonials.map((testimonial, index) => (
                  <div key={index} className="w-full flex-shrink-0 px-8">
                    <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8">
                      <div className="flex justify-center mb-4">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                        ))}
                      </div>
                      <p className="text-xl mb-6 text-gray-200 leading-relaxed text-right">"{testimonial.content}"</p>
                      <div className="text-right">
                        <div className="font-bold text-lg">{testimonial.name}</div>
                        <div className="text-purple-300">{testimonial.role}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="flex justify-center mt-6 space-x-reverse space-x-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === currentSlide ? 'bg-purple-400 scale-125' : 'bg-white/30 hover:bg-white/50'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-10 px-6 py-20">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-r from-purple-600/30 to-pink-600/30 backdrop-blur-sm border border-white/20 rounded-3xl p-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              جاهز لتحليل بياناتك؟
            </h2>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              انضم إلى آلاف المؤسسات التي تثق في محلل الاستبانات لتطوير برامجها التدريبية
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => setShowAuth(true)}
                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 transform hover:scale-105 hover:shadow-2xl"
              >
                ابدأ مجاناً الآن
              </button>
              <button className="border-2 border-white/30 hover:border-white/50 px-8 py-4 rounded-full font-medium text-lg transition-all duration-300 hover:bg-white/10">
                تواصل معنا
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 px-6 py-12 border-t border-white/20">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center space-x-reverse space-x-4 mb-6">
            <div className="w-10 h-10 bg-gradient-to-r from-purple-400 to-pink-400 rounded-lg flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold">محلل الاستبانات</span>
          </div>
          <p className="text-gray-400 mb-4">
            تم التطوير بواسطة أسامة صبري الطيطي - مركز التعلم الإلكتروني - جامعة الإسراء
          </p>
          <p className="text-gray-500 text-sm">
            © 2024 جميع الحقوق محفوظة - محلل استبانات ورش العمل التدريبية
          </p>
        </div>
      </footer>
    </div>
  );
};